import { useEffect, useId, useRef, useState } from 'react';
import { matchPath } from 'react-router-dom';
import {
  ArrowLeft,
  Bug,
  Camera,
  Check,
  CheckCircle2,
  Copy,
  Heart,
  Lightbulb,
  Loader2,
  Paperclip,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import type {
  FeedbackDispositivo,
  FeedbackEnvioResposta,
  FeedbackErroRecente,
  FeedbackTipo,
} from '@patrociconnect/shared';
import { API_URL, api, getErrorMessage } from '@/lib/api';
import { APP_VERSION } from '@/lib/errorReporting';
import type { FeedbackPrefill } from '@/lib/feedbackBus';
import { getRecentErrors } from '@/lib/recentErrors';
import { captureMaskedScreen } from '@/lib/screenCapture';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { Modal } from '@/components/ui/Modal';

interface TipoOption {
  value: FeedbackTipo;
  label: string;
  hint: string;
  icon: LucideIcon;
  placeholder: string;
}

/** Nomes exatos da seção 5.2 do escopo */
const TIPO_OPTIONS: TipoOption[] = [
  {
    value: 'bug',
    label: 'Reportar um problema',
    hint: 'Algo não funcionou como esperado',
    icon: Bug,
    placeholder: 'O que você fez, o que esperava e o que aconteceu? Ex.: cliquei em "Enviar proposta" e nada aconteceu.',
  },
  {
    value: 'sugestao',
    label: 'Sugerir uma melhoria',
    hint: 'Uma ideia para a plataforma',
    icon: Lightbulb,
    placeholder: 'Ex.: seria ótimo poder filtrar oportunidades por cidade e modalidade ao mesmo tempo.',
  },
  {
    value: 'feedback',
    label: 'Elogiar / comentar',
    hint: 'O que está achando até aqui',
    icon: Heart,
    placeholder: 'Conte o que está achando da plataforma — o que funciona bem e o que poderia ser diferente.',
  },
];

const DEFAULT_OPTION: TipoOption = TIPO_OPTIONS[1] as TipoOption;
const MIN_LENGTH = 3;
const MAX_LENGTH = 2000;
const OBJECT_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const COPIED_FEEDBACK_MS = 2000;

/** Rotas com :id — o id do objeto acompanha o feedback para a Triagem */
const ROUTES_WITH_ID = [
  '/asset/:id',
  '/perfil/:id',
  '/dashboard/criador-de-posts/:id',
  '/dashboard/criador-de-posts/:id/analytics',
];
const ID_QUERY_PARAMS = ['offerId', 'chatId', 'dealId'];

function resolveObjectId(pathname: string, search: string): string | undefined {
  for (const pattern of ROUTES_WITH_ID) {
    const id = matchPath(pattern, pathname)?.params.id;
    if (id && OBJECT_ID_PATTERN.test(id)) return id;
  }
  const params = new URLSearchParams(search);
  for (const key of ID_QUERY_PARAMS) {
    const value = params.get(key);
    if (value && OBJECT_ID_PATTERN.test(value)) return value;
  }
  return undefined;
}

/**
 * Categorias do dispositivo a partir do User-Agent — sem fingerprinting:
 * nada de fontes, plugins, canvas, fuso ou versões de SO detalhadas.
 */
function describeDevice(): FeedbackDispositivo {
  const ua = navigator.userAgent;
  const isTablet = /iPad|Tablet/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua));
  const isMobile = !isTablet && /Mobi|iPhone|Android/i.test(ua);
  const so = /Android/i.test(ua)
    ? 'Android'
    : /iPhone|iPad|iPod/i.test(ua)
      ? 'iOS'
      : /Windows/i.test(ua)
        ? 'Windows'
        : /Mac OS X/i.test(ua)
          ? 'macOS'
          : /Linux/i.test(ua)
            ? 'Linux'
            : undefined;
  const browserMatch =
    /(Edg|OPR|SamsungBrowser|Firefox|Chrome)\/(\d+)/.exec(ua) ?? (/Version\/(\d+).*Safari/.test(ua) ? ['', 'Safari', /Version\/(\d+)/.exec(ua)?.[1] ?? ''] : null);
  const browserNames: Record<string, string> = { Edg: 'Edge', OPR: 'Opera', SamsungBrowser: 'Samsung Internet' };
  const navegador = browserMatch ? `${browserNames[browserMatch[1] ?? ''] ?? browserMatch[1]} ${browserMatch[2]}`.trim() : undefined;
  return {
    tipo: isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop',
    ...(so ? { so } : {}),
    ...(navegador ? { navegador: navegador.slice(0, 60) } : {}),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  };
}

function formatErrorTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

async function uploadCapture(id: string, token: string, blob: Blob): Promise<void> {
  const form = new FormData();
  form.append('captura', blob, blob.type === 'image/jpeg' ? 'captura.jpg' : 'captura.png');
  const response = await fetch(`${API_URL}/feedback/${encodeURIComponent(id)}/captura`, {
    method: 'POST',
    headers: { 'x-captura-token': token },
    body: form,
  });
  if (!response.ok) throw new Error('Falha ao anexar a captura.');
}

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  prefill?: FeedbackPrefill;
}

type Step = 'escolher' | 'escrever' | 'enviado';

/**
 * Diálogo de feedback independente de layout/Toast: usado pelo botão fixo,
 * pelo ErrorBoundary e pela página 404. Dois toques: abrir → escolher o tipo
 * (já cai no campo de texto com foco). Lê a tela de window.location para
 * funcionar mesmo quando a árvore de rotas quebrou.
 */
export function FeedbackDialog({ isOpen, onClose, prefill }: FeedbackDialogProps) {
  const baseId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isLoggedIn = useAuthStore((state) => Boolean(state.user));
  const [step, setStep] = useState<Step>('escolher');
  const [tipo, setTipo] = useState<FeedbackTipo>('sugestao');
  const [texto, setTexto] = useState('');
  const [shouldIncludeTechnical, setShouldIncludeTechnical] = useState(true);
  const [shouldAttachCapture, setShouldAttachCapture] = useState(false);
  const [capture, setCapture] = useState<{ blob: Blob; url: string } | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [recentErrors, setRecentErrors] = useState<FeedbackErroRecente[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [codigo, setCodigo] = useState<string | null>(null);
  const [captureUploadFailed, setCaptureUploadFailed] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Cada abertura começa do prefill (ex.: "Reportar" num erro já vai para o texto)
  useEffect(() => {
    if (!isOpen) return;
    setStep(prefill?.tipo ? 'escrever' : 'escolher');
    setTipo(prefill?.tipo ?? 'sugestao');
    setError(null);
    setCodigo(null);
    setCaptureUploadFailed(false);
    setShouldIncludeTechnical(true);
    setShouldAttachCapture(false);
    setCaptureError(null);
    setRecentErrors(getRecentErrors());
  }, [isOpen, prefill?.tipo]);

  // Libera a prévia ao trocar/remover a captura
  useEffect(() => () => {
    if (capture) URL.revokeObjectURL(capture.url);
  }, [capture]);

  useEffect(() => {
    if (step === 'escrever' && isOpen) textareaRef.current?.focus();
  }, [step, isOpen]);

  useEffect(() => {
    if (!isCopied) return;
    const timer = window.setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
    return () => window.clearTimeout(timer);
  }, [isCopied]);

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const objectId = resolveObjectId(pathname, search);
  const hasErrorContext = Boolean(prefill?.erroMensagem || prefill?.correlationId || prefill?.erroId);
  const option: TipoOption = TIPO_OPTIONS.find((item) => item.value === tipo) ?? DEFAULT_OPTION;

  const discardCapture = () => {
    setCapture(null);
    setShouldAttachCapture(false);
  };

  const close = () => {
    if (isSending || isCapturing) return;
    if (step === 'enviado') setTexto('');
    discardCapture();
    onClose();
  };

  const chooseTipo = (value: FeedbackTipo) => {
    setTipo(value);
    setStep('escrever');
  };

  const handleCaptureToggle = async (checked: boolean) => {
    setCaptureError(null);
    if (!checked) {
      discardCapture();
      return;
    }
    setShouldAttachCapture(true);
    setIsCapturing(true);
    try {
      const blob = await captureMaskedScreen();
      setCapture({ blob, url: URL.createObjectURL(blob) });
    } catch (captureFailure) {
      console.error('Falha ao capturar a tela para o feedback', captureFailure);
      setShouldAttachCapture(false);
      setCaptureError('Não foi possível capturar a tela neste navegador. Você pode enviar sem a captura.');
    } finally {
      setIsCapturing(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (texto.trim().length < MIN_LENGTH) {
      setError(`Escreva pelo menos ${MIN_LENGTH} caracteres.`);
      textareaRef.current?.focus();
      return;
    }
    setError(null);
    setIsSending(true);
    try {
      const hasCapture = shouldAttachCapture && capture !== null;
      const resposta = await api.post<FeedbackEnvioResposta>(
        '/feedback',
        {
          tipo,
          texto: texto.trim(),
          ...(hasCapture ? { comCaptura: true } : {}),
          // Sem o consentimento da caixa, nada técnico é enviado — nem o vínculo com o erro
          ...(shouldIncludeTechnical
            ? {
                tela: pathname,
                versao: APP_VERSION,
                dispositivo: describeDevice(),
                ...(recentErrors.length ? { ultimosErros: recentErrors } : {}),
                ...(objectId ? { objectId } : {}),
                ...(prefill?.correlationId ? { correlationId: prefill.correlationId } : {}),
                ...(prefill?.erroId ? { erroId: prefill.erroId } : {}),
                ...(prefill?.erroMensagem ? { erroMensagem: prefill.erroMensagem.slice(0, 300) } : {}),
              }
            : {}),
        },
        { requireAuth: false, optionalAuth: true }
      );
      if (hasCapture && resposta.capturaToken) {
        try {
          await uploadCapture(resposta.id, resposta.capturaToken, capture.blob);
        } catch (uploadError) {
          // O relato já foi salvo; só a captura ficou de fora
          console.error('Falha ao anexar captura ao feedback', { ocorrenciaId: resposta.id, uploadError });
          setCaptureUploadFailed(true);
        }
      }
      setCodigo(resposta.codigo ?? null);
      setStep('enviado');
      setTexto('');
      discardCapture();
    } catch (submitError) {
      setError(getErrorMessage(submitError, 'Não foi possível enviar agora. Tente novamente em instantes.'));
    } finally {
      setIsSending(false);
    }
  };

  const copyCodigo = async () => {
    if (!codigo) return;
    try {
      await navigator.clipboard.writeText(codigo);
      setIsCopied(true);
    } catch (copyError) {
      console.error('Falha ao copiar número de acompanhamento', copyError);
    }
  };

  if (step === 'enviado') {
    return (
      <Modal
        isOpen={isOpen}
        onClose={close}
        title="Recebemos, obrigado!"
        footer={
          <button type="button" className="btn-primary" onClick={close} data-autofocus>
            Fechar
          </button>
        }
      >
        <div className="space-y-5" role="status">
          {codigo && (
            <div className="relative overflow-hidden rounded-2xl border border-brand-200 bg-brand-50 p-4 dark:border-brand-500/30 dark:bg-brand-500/10">
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-700 dark:text-brand-300">
                Seu número de acompanhamento:
              </p>
              <div className="mt-1.5 flex flex-wrap items-center justify-between gap-3">
                <span className="font-mono text-2xl font-bold tracking-[0.12em] text-ink">{codigo}</span>
                <button type="button" className="btn-secondary btn-sm" onClick={copyCodigo} aria-label={`Copiar número de acompanhamento ${codigo}`}>
                  {isCopied ? <Check className="h-4 w-4 text-success" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
                  {isCopied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
              <p className="mt-2 text-xs text-muted">Use este número para falar com a equipe sobre este envio.</p>
            </div>
          )}
          <ol className="space-y-2 border-l-2 border-brand-200 pl-4 text-sm text-muted dark:border-brand-500/30">
            <li><span className="font-medium text-ink">Leitura:</span> a equipe revisa todos os envios na triagem recorrente.</li>
            <li><span className="font-medium text-ink">Análise:</span> problemas são reproduzidos e ideias entram na fila de melhorias.</li>
            <li><span className="font-medium text-ink">Retorno:</span> {isLoggedIn ? 'avisamos aqui e por e-mail quando virar melhoria — "Você sugeriu, nós fizemos".' : 'entre na sua conta nas próximas vezes para acompanhar o andamento.'}</li>
          </ol>
          {captureUploadFailed && (
            <p className="text-xs text-warning">Seu relato foi salvo, mas a captura da tela não pôde ser anexada.</p>
          )}
          {isLoggedIn && (
            <a href="/dashboard/feedback" className="inline-flex text-sm font-semibold text-brand-600 underline-offset-4 hover:underline dark:text-brand-300">
              Acompanhar em "Minhas sugestões"
            </a>
          )}
        </div>
      </Modal>
    );
  }

  if (step === 'escolher') {
    return (
      <Modal
        isOpen={isOpen}
        onClose={close}
        size="lg"
        title="Feedback"
        description="Leva menos de um minuto. Lemos tudo e respondemos com um número de acompanhamento."
      >
        <ul className="grid gap-3 sm:grid-cols-3">
          {TIPO_OPTIONS.map((item, index) => {
            const Icon = item.icon;
            return (
              <li key={item.value}>
                <button
                  type="button"
                  onClick={() => chooseTipo(item.value)}
                  className="group flex h-full w-full items-center gap-3 rounded-2xl border-2 border-line p-4 text-left transition-all hover:-translate-y-0.5 hover:border-brand-400 hover:bg-brand-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 sm:flex-col sm:items-start sm:gap-3 dark:hover:bg-brand-500/10"
                  {...(index === 0 ? { 'data-autofocus': true } : {})}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ink/[0.05] text-ink/70 transition-colors group-hover:bg-brand-500 group-hover:text-on-brand" aria-hidden="true">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-semibold text-ink">{item.label}</span>
                    <span className="mt-0.5 block text-xs text-muted">{item.hint}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </Modal>
    );
  }

  const OptionIcon = option.icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      isBusy={isSending}
      size="lg"
      title={option.label}
      description="Sugestões, problemas e elogios são lidos pela equipe e viram melhorias na plataforma."
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={close} disabled={isSending}>
            Cancelar
          </button>
          <button type="submit" form={`${baseId}-form`} className="btn-primary" disabled={isSending || isCapturing}>
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Enviando...
              </>
            ) : (
              'Enviar'
            )}
          </button>
        </>
      }
    >
      <form id={`${baseId}-form`} onSubmit={handleSubmit} noValidate className="space-y-5">
        <div className="flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
            <OptionIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {option.label}
          </span>
          <button type="button" className="btn-ghost btn-sm" onClick={() => setStep('escolher')} disabled={isSending}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Trocar
          </button>
        </div>

        <div>
          <label htmlFor={`${baseId}-texto`} className="label">
            Sua mensagem
          </label>
          <textarea
            ref={textareaRef}
            id={`${baseId}-texto`}
            className="input min-h-[140px] resize-y"
            value={texto}
            maxLength={MAX_LENGTH}
            aria-invalid={error ? true : undefined}
            aria-describedby={`${baseId}-contador${error ? ` ${baseId}-erro` : ''}`}
            placeholder={option.placeholder}
            onChange={(event) => {
              setTexto(event.target.value);
              if (error) setError(null);
            }}
            data-autofocus
          />
          <div className="mt-1.5 flex justify-between gap-4 text-xs text-muted">
            <span>Não inclua senhas, CPF, documentos ou dados de terceiros.</span>
            <span id={`${baseId}-contador`}>
              {texto.length.toLocaleString('pt-BR')}/{MAX_LENGTH.toLocaleString('pt-BR')}
            </span>
          </div>
          {error && (
            <p id={`${baseId}-erro`} className="mt-1.5 text-sm text-danger" role="alert">
              {error}
            </p>
          )}
        </div>

        {hasErrorContext && shouldIncludeTechnical && (
          <div className="flex items-start gap-2 rounded-lg border border-dashed border-line bg-ink/[0.03] p-3 text-xs text-muted">
            <Paperclip className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <p>
              <span className="font-medium text-ink">Erro anexado:</span>{' '}
              {prefill?.erroMensagem ? `"${prefill.erroMensagem}"` : 'erro inesperado desta tela'}
              {prefill?.correlationId ? ` · código ${prefill.correlationId}` : ''}
            </p>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <input
              id={`${baseId}-captura`}
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-line text-brand-600 focus:ring-brand-500"
              checked={shouldAttachCapture}
              disabled={isCapturing || isSending}
              onChange={(event) => void handleCaptureToggle(event.target.checked)}
              aria-describedby={`${baseId}-captura-desc`}
            />
            <div>
              <label htmlFor={`${baseId}-captura`} className="text-sm font-medium text-ink">
                Anexar captura da tela
              </label>
              <p id={`${baseId}-captura-desc`} className="text-xs text-muted">
                Opcional. Campos de formulário, mensagens do chat, documentos e contatos saem cobertos. Você vê a imagem antes de enviar.
              </p>
            </div>
          </div>
          {isCapturing && (
            <p className="flex items-center gap-2 pl-7 text-xs text-muted" role="status">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Capturando a tela...
            </p>
          )}
          {captureError && (
            <p className="pl-7 text-xs text-danger" role="alert">{captureError}</p>
          )}
          {capture && (
            <figure className="ml-7 overflow-hidden rounded-xl border border-line bg-ink/[0.03]">
              <img src={capture.url} alt="Prévia da captura da tela com campos sensíveis cobertos" className="max-h-48 w-full object-contain object-top" />
              <figcaption className="flex items-center justify-between gap-2 border-t border-line px-3 py-2 text-xs text-muted">
                <span className="inline-flex items-center gap-1.5">
                  <Camera className="h-3.5 w-3.5" aria-hidden="true" />
                  Prévia — confira antes de enviar
                </span>
                <button type="button" className="btn-ghost btn-sm text-danger" onClick={discardCapture}>
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Remover
                </button>
              </figcaption>
            </figure>
          )}
        </div>

        <div className="flex items-start gap-3">
          <input
            id={`${baseId}-tecnico`}
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-line text-brand-600 focus:ring-brand-500"
            checked={shouldIncludeTechnical}
            onChange={(event) => setShouldIncludeTechnical(event.target.checked)}
            aria-describedby={`${baseId}-tecnico-desc`}
          />
          <div>
            <label htmlFor={`${baseId}-tecnico`} className="text-sm font-medium text-ink">
              Incluir informações técnicas
            </label>
            <p id={`${baseId}-tecnico-desc`} className="text-xs text-muted">
              Enviamos a tela atual ({pathname || '/'}), a versão do app ({APP_VERSION}), o tipo de dispositivo, sistema, navegador e tamanho da tela
              {objectId ? ', o identificador do item aberto' : ''}
              {hasErrorContext ? ', o código do erro' : ''}
              {recentErrors.length ? ` e ${recentErrors.length === 1 ? 'o último erro' : `os últimos ${recentErrors.length} erros`} desta sessão` : ''}. Nenhum conteúdo de chat ou dado pessoal é enviado automaticamente.
            </p>
            {shouldIncludeTechnical && recentErrors.length > 0 && (
              <details className="mt-1.5 text-xs text-muted">
                <summary className="cursor-pointer font-medium text-ink/80">Ver erros anexados</summary>
                <ul className="mt-1 space-y-0.5">
                  {recentErrors.map((item, index) => (
                    <li key={`${item.em}-${index}`} className="break-words">
                      {formatErrorTime(item.em)} · {item.mensagem}
                      {item.correlationId ? ` · ${item.correlationId}` : ''}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>

        <p className="border-t border-line pt-4 text-xs text-muted">
          Usamos seu envio apenas para melhorar a plataforma, conforme a{' '}
          <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="font-medium text-brand-600 underline-offset-2 hover:underline dark:text-brand-300">
            Política de Privacidade
          </a>
          .{isLoggedIn ? ' Você poderá acompanhar a resposta em "Minhas sugestões".' : ''}
        </p>
      </form>
    </Modal>
  );
}
